import {
  Box,
  ClickAwayListener,
  Fade,
  Popper,
  PopperProps,
} from "@mui/material";
import { styled } from "@mui/system";
import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { useMobileContext } from "../context/MobileContext";
import { toolTipEventBus } from "../utilities/toolTipEventBus";

interface CustomPopperProps {
  id?: string;
  title: ReactNode;
  children: ReactElement;
  placement?: PopperProps["placement"];
  isOnModal?: boolean;
  className?: string;
}

const StyledPopperContent = styled(Box)(() => ({
  backgroundColor: "#fff",
  color: "#344054",
  boxShadow: "0 -4px 5px -4px #FFC600, 0 4px 5px rgba(0, 0, 0, 0.3)",
  borderRadius: 6,
  position: "relative",
  maxWidth: "300px",
  padding: "12px",
  fontSize: "0.75rem",
  fontWeight: 400,
  whiteSpace: "pre-line",
}));

const Arrow = styled(Box)<{ placement?: string }>(({ placement }) => {
  const isTop = placement?.startsWith("top");
  const isBottom = placement?.startsWith("bottom");
  const isLeft = placement?.startsWith("left");
  const isRight = placement?.startsWith("right");

  return {
    position: "absolute",
    width: 0,
    height: 0,
    borderStyle: "solid",
    ...(isTop && {
      bottom: -6,
      left: "50%",
      transform: "translateX(-50%)",
      borderWidth: "6px 6px 0 6px",
      borderColor: "#fff transparent transparent transparent",
      filter: "drop-shadow(0 2px 2px rgba(0, 0, 0, 0.2))",
    }),
    ...(isBottom && {
      top: -6,
      left: "50%",
      transform: "translateX(-50%)",
      borderWidth: "0 6px 6px 6px",
      borderColor: "transparent transparent #fff transparent",
      filter: "drop-shadow(0 -2px 2px rgba(255, 198, 0, 0.3))",
    }),
    ...(isLeft && {
      right: -6,
      top: "50%",
      transform: "translateY(-50%)",
      borderWidth: "6px 0 6px 6px",
      borderColor: "transparent transparent transparent #fff",
      filter: "drop-shadow(2px 0 2px rgba(0, 0, 0, 0.2))",
    }),
    ...(isRight && {
      left: -6,
      top: "50%",
      transform: "translateY(-50%)",
      borderWidth: "6px 6px 6px 0",
      borderColor: "transparent #fff transparent transparent",
      filter: "drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.2))",
    }),
  };
});

const CustomPopper = ({
  id,
  title,
  children,
  placement = "top",
  isOnModal = false,
  className,
}: CustomPopperProps) => {
  const { isTouchDevice } = useMobileContext();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (isTouchDevice) {
      toolTipEventBus.emit("tooltip-event-close-all");
      setTimeout(() => setOpen((prev) => !prev), 0);
    }
  };

  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setOpen(false);
    }
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isTouchDevice) {
      const handleCloseAll = () => {
        setOpen(false);
      };
      toolTipEventBus.on("tooltip-event-close-all", handleCloseAll);
      return () => {
        toolTipEventBus.off("tooltip-event-close-all", handleCloseAll);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOffset = (): [number, number] => {
    if (placement?.startsWith("top")) return [0, 15];
    if (placement?.startsWith("bottom")) return [0, 15];
    if (placement?.startsWith("left")) return [0, 15];
    if (placement?.startsWith("right")) return [0, 15];
    return [0, 15];
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        ref={anchorRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: "inline-flex" }}
      >
        {children}
        <Popper
          id={id ? `${id}-custompopper` : undefined}
          open={open}
          anchorEl={anchorRef.current}
          placement={placement}
          transition
          className={className}
          style={{ zIndex: isOnModal ? 1300 : 1000 }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: getOffset(),
              },
            },
          ]}
        >
          {({ TransitionProps, placement: actualPlacement }) => (
            <Fade {...TransitionProps} timeout={200}>
              <StyledPopperContent>
                <Arrow placement={actualPlacement} />
                {title}
              </StyledPopperContent>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default CustomPopper;
